import { useEffect, useState } from 'react';

import { useWebSocket } from 'global/WebSocketContext';
import { Notification } from 'types/notificationsTypes';

interface UseNotificationTooltipReturn {
  showTooltip: boolean;
  tooltipNotification: Notification | null;
  handleTooltipClose: () => void;
}

// Estado global para evitar duplicação de tooltips
let globalLastMessageId: string | null = null;
let globalShowTooltip = false;
let globalTooltipNotification: Notification | null = null;

const listeners: Set<(show: boolean, notification: Notification | null) => void> = new Set();

const notifyListeners = (show: boolean, notification: Notification | null) => {
  listeners.forEach(listener => listener(show, notification));
};

export const useNotificationTooltip = (): UseNotificationTooltipReturn => {
  const { lastMessage, isConnected } = useWebSocket();
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipNotification, setTooltipNotification] = useState<Notification | null>(null);

  // Detectar novas notificações via WebSocket
  useEffect(() => {
    if (!isConnected || !lastMessage) return;

    // Só processa se for uma nova mensagem
    if (lastMessage.id !== globalLastMessageId) {
      console.log('[useNotificationTooltip] New notification received via WebSocket:', lastMessage);
      globalLastMessageId = lastMessage.id;

      // Mostrar tooltip para nova notificação
      globalTooltipNotification = lastMessage;
      globalShowTooltip = true;

      // Notificar todos os listeners
      notifyListeners(true, lastMessage);
    }
  }, [isConnected, lastMessage]);

  // Escutar mudanças globais
  useEffect(() => {
    const listener = (show: boolean, notification: Notification | null) => {
      setShowTooltip(show);
      setTooltipNotification(notification);
    };

    listeners.add(listener);

    // Sincronizar estado inicial
    setShowTooltip(globalShowTooltip);
    setTooltipNotification(globalTooltipNotification);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  const handleTooltipClose = () => {
    globalShowTooltip = false;
    globalTooltipNotification = null;
    setShowTooltip(false);
    setTooltipNotification(null);
    notifyListeners(false, null);
  };

  return {
    showTooltip,
    tooltipNotification,
    handleTooltipClose,
  };
};
